import React, { useState } from 'react'
import { useUser } from 'util/UserContext'
import { Info, Search, Check, Link } from 'lucide-react'
import Modal from 'ui/Modal'
import InputField from 'ui/InputField'
import { updateUserLinks } from 'util/userUtils'
import { toast } from 'sonner'
import InputTextArea from 'ui/InputTextArea'
import ActorPrivacy from 'components/Common/ActorPrivacy'

type SocialLinkType = 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit';

interface SocialLink {
    type: SocialLinkType;
    label: string;
    url: string;
}

interface Props {
    onClose: () => void
}

const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;

const SOCIAL_LINK_OPTIONS: Array<{
    type: SocialLinkType;
    label: string;
    icon?: string | React.ReactNode;
    placeholder: string;
    validation?: (value: string) => { isValid: boolean; error?: string };
    formatValue?: (value: string) => string;
    formatUrl?: (value: string) => string;
}> = [
    {
        type: 'custom',
        label: 'Custom URL',
        placeholder: 'https://example.com',
        icon: <Link className="size-4" />,
        validation: (value) => {
            try {
                new URL(value);
                return { isValid: true };
            } catch {
                return { isValid: false, error: 'Please enter a valid URL' };
            }
        }
    },
    {
        type: 'instagram',
        label: 'Instagram username',
        placeholder: 'igusername',
        icon: 'instagram_icon.png',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://www.instagram.com/${value}`
    },
    {
        type: 'x',
        label: 'X/Twitter username',
        placeholder: 'xusername',
        icon: 'x_icon.png',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://x.com/${value}`
    },
    {
        type: 'facebook',
        label: 'Facebook URL',
        placeholder: 'https://www.facebook.com/facebookpage',
        icon: 'facebook_icon.svg',
        validation: (value) => {
            const regex = /^https:\/\/(?:.*\.)?(?:facebook\.com|fb\.com)\/.+/i;
            return {
                isValid: regex.test(value),
                error: 'Please enter a valid Facebook URL'
            };
        }
    },
    {
        type: 'youtube',
        label: 'YouTube username',
        placeholder: '@youtubeusername',
        icon: 'youtube_icon.svg',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://www.youtube.com/@${value}`
    },
    {
        type: 'tiktok',
        label: 'TikTok username',
        placeholder: '@tiktokusername',
        icon: 'tiktok_icon_new.svg',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://www.tiktok.com/@${value}`
    },
    {
        type: 'reddit',
        label: 'Reddit username or subreddit',
        placeholder: 'u/redditusername OR r/subreddit',
        icon: 'reddit_icon.svg',
        validation: (value) => {
            const regex = /^[ru]\/[a-zA-Z0-9_-]+$/;
            return {
                isValid: regex.test(value),
                error: 'Please enter a valid Reddit username (u/username) or subreddit (r/subreddit)'
            };
        },
        formatUrl: (value) => `https://www.reddit.com/${value}`
    }
];

const MAX_LINKS = 9;

const UserAboutLinksModal: React.FC<Props> = ({ onClose }) => {
    const { currentUser, refreshUser } = useUser();
    const [links, setLinks] = useState<SocialLink[]>(currentUser?.properties?.about_bio_links?.value || []);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedType, setSelectedType] = useState<SocialLinkType | null>(null);
    const [selectedLink, setSelectedLink] = useState<SocialLink | null>(null);
    const [value, setValue] = useState('');
    const [linkLabel, setLinkLabel] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const filteredOptions = SOCIAL_LINK_OPTIONS.filter(option => 
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleTypeSelect = (type: SocialLinkType, existingLink?: SocialLink) => {
        setSelectedType(type);
        if (existingLink) {
            setSelectedLink(existingLink);
            if (type === 'custom' || type === 'facebook') {
                setValue(existingLink.url);
                setLinkLabel(existingLink.label);
            } else {
                setValue(existingLink.label);
            }
        } else {
            setSelectedLink(null);
            setValue('');
            setLinkLabel('');
        }
        setError(null);
    };

    const handleValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
        setError(null);
    };

    const handleLinkLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLinkLabel(e.target.value);
    };

    const handleDelete = async () => {
        if (!selectedLink) return;

        setIsDeleting(true);
        try {
            const updatedLinks = links.filter(link => link !== selectedLink);
            await updateUserLinks(updatedLinks);
            await refreshUser();
            setLinks(updatedLinks);
            toast.success('Link deleted successfully!');
            setSelectedType(null);
            setSelectedLink(null);
            setValue('');
            setLinkLabel('');
        } catch (error) {
            console.error('Error deleting link:', error);
            setError('Failed to delete link. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSaveLink = async () => {
        if (!selectedType) return;

        const option = SOCIAL_LINK_OPTIONS.find(opt => opt.type === selectedType);
        if (!option) return;

        // Validate value
        if (option.validation) {
            const { isValid, error } = option.validation(value);
            if (!isValid) {
                setError(error || 'Invalid input');
                return;
            }
        }

        // Format value and URL
        const formattedValue = option.formatValue ? option.formatValue(value) : value;
        const url = option.formatUrl ? option.formatUrl(formattedValue) : formattedValue;

        const newLink: SocialLink = {
            type: selectedType,
            label: selectedType === 'custom' || selectedType === 'facebook' ? linkLabel : formattedValue,
            url
        };

        let updatedLinks: SocialLink[];
        if (selectedLink) {
            updatedLinks = links.map(link => link === selectedLink ? newLink : link);
        } else {
            updatedLinks = [...links, newLink];
        }

        setError(null);
        setIsSubmitting(true);

        try {
            await updateUserLinks(updatedLinks);
            await refreshUser();
            setLinks(updatedLinks);
            toast.success('Link saved successfully!');
            setSelectedType(null);
            setSelectedLink(null);
            setValue('');
        } catch (error) {
            console.error('Error updating social links:', error);
            setError('Failed to update social link. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderMainModal = () => (
        <>
            <div className="flex flex-col flex-grow space-y-4">
                <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    Add up to {MAX_LINKS} links to display on your profile.
                </p>

                <div className="relative mx-1">
                    <div className="relative border border-eoe-border dark:border-eoe-dark-border rounded-full">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-eoe-text-meta dark:text-eoe-dark-text-meta" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            placeholder="Search"
                            className="w-full pl-10 pr-4 py-2 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-full text-sm text-eoe-text dark:text-eoe-dark-text"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    {/* Show all existing links first */}
                    {links.map((link, index) => {
                        const option = SOCIAL_LINK_OPTIONS.find(opt => opt.type === link.type);
                        if (!option) return null;
                        let linkIcon = `${IMG_CDN}/icons/link_icon.svg`; // Default fallback icon
                        if (option.type === 'custom') {
                            try {
                                const url = new URL(link.url);
                                linkIcon = `https://${url.hostname}/favicon.ico`;
                            } catch (error) {
                                console.error("Error parsing URL:", error);
                                // Keep using the default fallback icon
                            }
                        }
                        
                        return (
                            <button
                                key={`selected-${link.type}-${index}`}
                                onClick={() => handleTypeSelect(link.type, link)}
                                className="w-full flex items-center justify-between p-3 rounded-lg text-sm bg-eoe-bg-hover dark:bg-eoe-dark-icon-hover"
                            >
                                <span className="flex flex-row items-center text-eoe-text dark:text-eoe-dark-text gap-x-2">
                                    {option.type === 'custom' ? (
                                        <img 
                                            className={`size-4 rounded-sm`} 
                                            src={linkIcon} 
                                            alt={`Website icon`}
                                            onError={(e) => {
                                                e.currentTarget.onerror = null; // Prevent infinite fallback loop
                                                try {
                                                    // Try faviconkit as the first fallback
                                                    const url = new URL(link.url);
                                                    e.currentTarget.src = `https://api.faviconkit.com/${url.hostname}`;
                                                } catch (error) {
                                                    // If that fails too, use the generic link icon
                                                    e.currentTarget.src = `${option.icon}`;
                                                }
                                            }}
                                        />
                                    ) : (
                                        <img className={`size-4 ${option.type === 'tiktok' || option.type === 'x' ? 'bg-white rounded-full' : ''}`} src={`${IMG_CDN}/icons/${option.icon}`} alt={option.label + ' icon'} />
                                    )}
                                    {link.label}
                                </span>
                                <Check className="size-4 text-eoe-text dark:text-eoe-dark-text" />
                            </button>
                        );
                    })}

                    {/* Then show all available options */}
                    {filteredOptions.map(option => {
                        const isSelected = links.some(link => link.type === option.type);

                        return (
                            <button
                                key={`option-${option.type}`}
                                onClick={() => handleTypeSelect(option.type)}
                                disabled={links.length >= MAX_LINKS}
                                className={`w-full flex items-center justify-between p-3 rounded-lg text-sm hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-icon-hover ${
                                    links.length >= MAX_LINKS ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                <span className="flex flex-row items-center text-eoe-text dark:text-eoe-dark-text gap-x-2">
                                    {option.type === 'custom' ? (
                                        option.icon
                                    ) : (
                                        <img className={`size-4 ${option.type === 'tiktok' || option.type === 'x' ? 'bg-white rounded-full' : ''}`} src={`${IMG_CDN}/icons/${option.icon}`} alt={option.label + ' icon'} />
                                    )}
                                    {isSelected ? `${option.label.split(' ')[0]}` : option.label.split(' ')[0]}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="flex flex-row justify-between items-center">
                    <ActorPrivacy 
                        privacy={'public'}
                        settingKey="about_bio_links"
                        onPrivacyChange={() => {}}
                        actorType="user"
                        editMode={false}
                        showDropdown={false}
                        miniIcon={false}
                        disabled={true}
                        className="opacity-50"
                    />
                    <div className="place-self-end text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                        {links.length}/{MAX_LINKS} links added
                    </div>
                </div>

            </div>

            <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary w-full sm:w-fit py-2 px-4"
                >
                    Close
                </button>
            </div>
        </>
    );

    const renderEditModal = () => {
        if (!selectedType) return null;
        const option = SOCIAL_LINK_OPTIONS.find(opt => opt.type === selectedType);
        if (!option) return null;

        return (
            <>
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Add your {option.label} link.
                    </p>

                    <div className="form-control space-y-2">
                        <InputTextArea
                            id="linkValue"
                            name="linkValue"
                            labelText={option.label}
                            fieldPlaceholder={option.placeholder}
                            value={value}
                            onChange={handleValueChange}
                            spellCheck={false}
                            fieldRequired
                            autoComplete="off"
                            minLength={1}
                        />
                        {(option.type === 'custom' || option.type === 'facebook') && (                            
                            <InputField
                            type="text"
                            id="linkLabel"
                            name="linkLabel"
                            labelText="Display text"
                            fieldPlaceholder="Display text"
                            value={linkLabel}
                            onChange={handleLinkLabelChange}
                            spellCheck={false}
                            fieldRequired
                            autoComplete="off"
                            maxLength={30}
                            />
                        )}
                    </div>

                    {error && (
                        <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                            <Info className="mr-1 size-4" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center pb-8 space-y-2 space-y-reverse sm:space-y-0">
                    {selectedLink && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="btn-danger w-full sm:w-fit py-2 px-4"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete link'}
                        </button>
                    )}
                    <div className="flex flex-col space-y-2 sm:space-y-0 sm:space-x-2 sm:flex-row">
                        <button
                            type="button"
                            onClick={() => {
                                setSelectedType(null);
                                setSelectedLink(null);
                                setValue('');
                                setLinkLabel('');
                            }}
                            className="btn-secondary w-full sm:w-fit py-2 px-4"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSaveLink}
                            disabled={isSubmitting || isDeleting || !value}
                            className={`w-full sm:w-fit py-2 px-4 ${
                                isSubmitting || isDeleting || !value ? 'btn-primary-disabled' : 'btn-primary'
                            }`}
                        >
                            {isSubmitting && !selectedLink ? 'Submitting...' : 'Save'}
                        </button>
                    </div>
                </div>
            </>
        );
    };

    return (
        <Modal 
            isOpen={true} 
            onClose={onClose} 
            title="External links"
            onBack={selectedType ? () => {
                setSelectedType(null);
                setSelectedLink(null);
                setValue('');
            } : undefined}
        >
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                {selectedType ? renderEditModal() : renderMainModal()}
            </div>
        </Modal>
    );
};

export default UserAboutLinksModal; 